/**
 * Where a request to an `/auth/*` page should go when the visitor already has a
 * session.
 *
 * A signed-in visitor usually has no business on the login page, so the
 * middleware bounces them into the app. The catch is that some `/auth` links
 * carry the *reason* they were opened in the query string, and bouncing to a
 * bare `/app` threw it away:
 *
 *   * Organization invitations are emailed as
 *     `/auth/login?invitationId=…&email=…` — the accept/decline screen is only
 *     reachable through that id. An existing customer invited into a second
 *     organization is signed in when they click, so they were redirected to
 *     `/app`, which the `/app` branch immediately resolves to the organization
 *     they were *already* in. The invitation appeared to do nothing, stayed
 *     `pending` forever, and nothing else in the product can accept it.
 *   * `redirectTo` is set whenever the middleware sends an unauthenticated
 *     request to the login page, and survives a sign-in the same way.
 *
 * Everything here is pure so it can run in the Edge middleware and be
 * unit-tested without a request object.
 */

/** Route that renders an organization invitation with its accept/decline buttons. */
export const ORGANIZATION_INVITATION_PATH = "/app/organization-invitation";

/** Where a signed-in visitor with nothing else to go on belongs. */
export const DEFAULT_SIGNED_IN_PATH = "/app";

/**
 * The one `/auth` page a signed-in user has a reason to be on: better-auth's
 * reset link may well be opened in a browser that is already authenticated.
 */
const SIGNED_IN_ALLOWED_AUTH_PATHS: readonly string[] = [
	"/auth/reset-password",
];

/**
 * better-auth ids are nanoid/cuid-shaped. The id becomes a path segment, so
 * anything outside that alphabet is refused rather than escaped — a value
 * carrying `/`, `.` or `%` could otherwise walk out of the route.
 */
const INVITATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/**
 * The invitation screen for `invitationId`, or `null` when the id is not one we
 * would ever have issued.
 */
export function organizationInvitationPath(
	invitationId: string | null | undefined,
): string | null {
	return invitationId && INVITATION_ID_PATTERN.test(invitationId)
		? `${ORGANIZATION_INVITATION_PATH}/${invitationId}`
		: null;
}

/**
 * True for a path we are willing to send a signed-in visitor to.
 *
 * `redirectTo` arrives in the query string and is therefore attacker-controlled:
 * it has to stay an app-relative path on our own origin. Browsers read both
 * `//evil.com` and `/\evil.com` as protocol-relative URLs, and anything
 * carrying a scheme obviously leaves the site — so require a single leading
 * slash and nothing that looks like an authority.
 */
export function isSafeRedirectPath(path: string | null | undefined): boolean {
	return (
		!!path &&
		path.startsWith("/") &&
		!path.startsWith("//") &&
		!path.startsWith("/\\")
	);
}

/**
 * Resolve the destination for a signed-in visitor hitting an `/auth` page.
 *
 * @returns the path to redirect to, or `null` to let the request render.
 */
export function signedInAuthRedirect(
	pathname: string,
	searchParams: URLSearchParams,
): string | null {
	if (SIGNED_IN_ALLOWED_AUTH_PATHS.includes(pathname)) {
		return null;
	}

	// An invitation outranks `redirectTo`: the invitation email sets both when
	// the recipient was bounced through the login page, and joining the
	// organization is the thing they actually clicked.
	const invitationPath = organizationInvitationPath(
		searchParams.get("invitationId"),
	);

	if (invitationPath) {
		return invitationPath;
	}

	const redirectTo = searchParams.get("redirectTo");

	return isSafeRedirectPath(redirectTo)
		? (redirectTo as string)
		: DEFAULT_SIGNED_IN_PATH;
}
